import PropTypes from "prop-types";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { get } from "lodash";

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "@/components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";

import { REQUIRED_MARK } from "./fieldClasses";

const FormMultiSelect = ({
  name,
  label,
  required,
  options,
  placeholder = "Select…",
  disabled,
  id,
  renderOption,
  getChipStyle,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;

  // base-ui derives display/value from a `{ value, label }` item shape, and
  // matches selection by reference. Keeping one stable item per option (and
  // looking selections up in the same set) lets the RHF string array map to
  // those items without custom equality.
  const items = useMemo(
    () => options.map(o => ({ value: o.value, label: o.text, option: o })),
    [options]
  );
  const itemLabel = it => (renderOption ? renderOption(it.option) : it.label);
  const byValue = useMemo(
    () => new Map(items.map(it => [it.value, it])),
    [items]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const current = Array.isArray(field.value) ? field.value : [];
        const selectedItems = current.map(v => byValue.get(v)).filter(Boolean);
        // Already-selected options are removable via their chips, so drop them
        // from the dropdown instead of re-listing them with a checkmark.
        const selectedValues = new Set(current);
        const availableItems = items.filter(
          it => !selectedValues.has(it.value)
        );
        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={inputId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <Combobox
              items={availableItems}
              value={selectedItems}
              onValueChange={next =>
                field.onChange(
                  (Array.isArray(next) ? next : []).map(it => it.value)
                )
              }
              multiple
              disabled={disabled}
            >
              <ComboboxChips>
                <ComboboxValue>
                  {selected =>
                    selected.map(it => (
                      <ComboboxChip
                        key={it.value}
                        style={getChipStyle?.(it.option)}
                      >
                        {it.label}
                      </ComboboxChip>
                    ))
                  }
                </ComboboxValue>
                <ComboboxChipsInput
                  id={inputId}
                  placeholder={current.length === 0 ? placeholder : ""}
                  required={required && current.length === 0}
                  aria-invalid={error ? true : undefined}
                  onBlur={field.onBlur}
                />
              </ComboboxChips>
              <ComboboxContent>
                <ComboboxEmpty>No matches</ComboboxEmpty>
                <ComboboxList>
                  {item => (
                    <ComboboxItem key={item.value} value={item}>
                      {itemLabel(item)}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormMultiSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  clearable: PropTypes.bool,
  search: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  renderOption: PropTypes.func,
  getChipStyle: PropTypes.func,
};

export default FormMultiSelect;
